import { FC, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useMediaQuery } from "context/media/MediaContext";
import { DIGIPOSTHEME } from "theme/digiPosTheme";

import {
  Flex,
  Panel,
  PanelHeader,
  TextField,
  Button,
  Spinner,
} from "components";
import { IPasswordChangeRequest } from "models/Password/password.model";
import { yupResolver } from "@hookform/resolvers/yup";
import { passChangeSubmitValidationSchema } from "validations/password.validation";
import { passwordChangeAsync } from "services/password/password.service";
import { TokenInclude } from "helpers/GetToken";
import Alert from "components/BaseComponents/Alert/Alert";
import { requestDataChangeDefaultValues } from "./PassForm.constant";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { setMenuClick } from "redux/slices/StepSlice";

const PasswordChange: FC<{}> = () => {
  const { t } = useTranslation();
  const { isMobile } = useMediaQuery();
  const navigate = useNavigate();
  const token = TokenInclude();
  const dispatch = useAppDispatch();
  const { previousMenuClickId } = useAppSelector((state) => state.step);

  const [isAddSuccess, setIsAddSuccess] = useState<boolean>(false);
  const [isAddError, setIsAddError] = useState<boolean>(false);
  const [txt, setTxt] = useState<string>(t("success_add_message").toString());

  const {
    handleSubmit,
    control,
    errors,
    reset,
    formState: { isSubmitting },
  } = useForm<IPasswordChangeRequest>({
    defaultValues: requestDataChangeDefaultValues,
    resolver: yupResolver(passChangeSubmitValidationSchema(t)),
  });
  const onSubmit = async (data: IPasswordChangeRequest): Promise<void> => {
    const { oldPassword, newPassword } = data;
    const retVal = await passwordChangeAsync(token, oldPassword!, newPassword!);
    if (retVal.confirm) {
      setIsAddSuccess(true);
    } else {
      const errMessage: any = retVal;
      setIsAddError(true);
      setTxt(errMessage);
      return;
    }
    reset();
  };

  const goBack = () => {
    dispatch(setMenuClick(previousMenuClickId));
    navigate(-1);
  };

  return (
    <Panel width='646px' marginTop='0'>
      {(isAddSuccess || isAddError) && (
        <Alert
          alertType={isAddSuccess ? "Success" : "Error"}
          duration={6000}
          isVisible={isAddSuccess || isAddError}
          message={txt}
          setIsAddSuccess={setIsAddSuccess}
          setIsAddError={setIsAddError}
        />
      )}
      <Spinner show={isSubmitting} />
      <PanelHeader title={t("change_passsword_enter_password")} />

      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Flex.StyledFormWrapper>
          <Flex.Row
            alignItems='flex-start'
            direction={isMobile ? "column-reverse" : "row"}
            noMargin
          >
            <Flex.Col>
              <Flex.Row>
                <Flex.Col>
                  <TextField
                    control={control}
                    error={errors.oldPassword?.message}
                    label={t("old_password").toString()}
                    maxLength={6}
                    name='oldPassword'
                    type='masked'
                  />
                </Flex.Col>
              </Flex.Row>

              <Flex.Row>
                <Flex.Col>
                  <TextField
                    control={control}
                    error={errors.newPassword?.message}
                    label={t("new_password").toString()}
                    maxLength={6}
                    name='newPassword'
                    type='masked'
                  />
                </Flex.Col>
              </Flex.Row>

              <Flex.Row>
                <Flex.Col>
                  <TextField
                    control={control}
                    error={errors.newPasswordAgain?.message}
                    label={t("new_password_repeat").toString()}
                    maxLength={6}
                    name='newPasswordAgain'
                    type='masked'
                  />
                </Flex.Col>
              </Flex.Row>
            </Flex.Col>
          </Flex.Row>
        </Flex.StyledFormWrapper>

        <Flex.Row
          backgroundColor={DIGIPOSTHEME.colors.secondaryOffWhite}
          justifyContent='flex-end'
          isResponsive
          noMargin
          gap
        >
          <Flex.Col>
            <Button
              type='button'
              width={isMobile ? "100%" : "100px"}
              text={`← ${t("back")}`}
              onClick={() => goBack()}
              color='light'
            />
          </Flex.Col>
          <Flex.Col offSet={22} size={0.5}>
            <Button text={t("continue").toString()} type='submit' />
          </Flex.Col>
        </Flex.Row>
      </form>
    </Panel>
  );
};

export default PasswordChange;
